import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  creerBrouillonModele,
  enregistrerBrouillonModele,
  listerModelesContrat,
  publierModele,
  type ChampsModele,
} from '../../api/candidatures'
import { messageErreur } from '../../onboarding/erreurs'
import {
  apercuModele,
  controlerModele,
  IDENTITES_EXEMPLE,
  MARQUES_PROPOSEES,
} from '../../onboarding/modele-controle'
import { LIBELLE_STATUT } from '../../onboarding/identite'
import type { ModeleContrat } from '../../onboarding/types'
import { dateCourte } from '../Format'
import { Modale } from '../Modale'
import { Erreur, Info, useAction } from './Briques'
import { MarkdownRendu } from './MarkdownRendu'

const LIBELLE_KIND = { livreur: 'Contrat livreur', commercant: 'Contrat commerçant' } as const
const KINDS = ['livreur', 'commercant'] as const

const NOUVEAU_TEXTE = '# Titre du contrat\n\nRédigez ici le contrat en markdown.\n\n## Article 1\n\n- point un\n- point deux\n'

/**
 * Panneau « Modèles de contrat » — RÉSERVÉ AUX ADMINS (les policies de la base
 * aussi). Versions par type, brouillons éditables, publication. Une version
 * publiée est IMMUABLE : l'édition est désactivée.
 */
export function ModelesContrat({ ouvert, surFermer }: { ouvert: boolean; surFermer: () => void }) {
  const [modeles, setModeles] = useState<ModeleContrat[]>([])
  const [erreurLecture, setErreurLecture] = useState<string | null>(null)
  const [choisiId, setChoisiId] = useState<string | null>(null)

  const charger = useCallback(async (selection?: string) => {
    try {
      const liste = await listerModelesContrat()
      setModeles(liste)
      setErreurLecture(null)
      if (selection) setChoisiId(selection)
    } catch (e) {
      setErreurLecture(messageErreur(e))
    }
  }, [])

  useEffect(() => {
    if (ouvert) void charger()
  }, [ouvert, charger])

  const choisi = modeles.find((m) => m.id === choisiId) ?? null

  return (
    <Modale titre="Modèles de contrat" ouverte={ouvert} large surFermer={surFermer}>
      <div className="dos-alerte" style={{ marginTop: 0, marginBottom: 12 }}>
        Les contrats livrés avec l'application sont des <b>brouillons PROVISOIRES</b> à remplacer par un texte relu par un
        avocat. Tant que « texte provisoire » est coché, la publication est refusée.
      </div>
      <Erreur texte={erreurLecture} />
      <div className="mod-grille">
        <div className="mod-liste">
          {KINDS.map((kind) => (
            <div key={kind}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '6px 0' }}>
                <b style={{ fontSize: 12.5 }}>{LIBELLE_KIND[kind]}</b>
                <NouveauBrouillon
                  kind={kind}
                  dernier={modeles.find((m) => m.kind === kind) ?? null}
                  surCree={(m) => void charger(m.id)}
                />
              </div>
              {modeles.filter((m) => m.kind === kind).map((m) => (
                <button
                  key={m.id}
                  className={`mod-item${m.id === choisiId ? ' on' : ''}`}
                  onClick={() => setChoisiId(m.id)}
                >
                  <b>Version {m.version}</b> — {m.titre}
                  <span className="sous">
                    {m.publieLe ? (
                      <span className="badge badge-green">Publiée {dateCourte(m.publieLe)}</span>
                    ) : (
                      <span className="badge badge-yellow">Brouillon</span>
                    )}
                    {m.provisoire && <span className="badge badge-red">Provisoire</span>}
                  </span>
                </button>
              ))}
              {!modeles.some((m) => m.kind === kind) && <p className="dos-note">Aucune version.</p>}
            </div>
          ))}
        </div>

        <div>
          {choisi ? (
            <EditeurModele key={choisi.id} modele={choisi} surChange={() => void charger(choisi.id)} />
          ) : (
            <p className="dos-note">Choisissez une version à gauche, ou créez un brouillon.</p>
          )}
        </div>
      </div>
    </Modale>
  )
}

function NouveauBrouillon({
  kind,
  dernier,
  surCree,
}: {
  kind: 'livreur' | 'commercant'
  dernier: ModeleContrat | null
  surCree: (m: ModeleContrat) => void
}) {
  const { occupe, erreur, lancer } = useAction()
  return (
    <>
      <button
        className="btn btn-outline btn-sm"
        disabled={occupe}
        title="Nouvelle version = dernière version + 1, copiée de la dernière pour repartir de son texte"
        onClick={() =>
          void lancer(async () => {
            const champs: ChampsModele = dernier
              ? {
                  titre: dernier.titre,
                  corps: dernier.corps,
                  exclusiviteMois: dernier.exclusiviteMois,
                  provisoire: dernier.provisoire,
                }
              : {
                  titre: kind === 'livreur' ? 'Contrat de partenariat livreur' : 'Contrat de partenariat commerçant',
                  corps: NOUVEAU_TEXTE,
                  exclusiviteMois: null,
                  provisoire: true,
                }
            surCree(await creerBrouillonModele(kind, champs))
          })
        }
      >
        + Brouillon
      </button>
      {erreur && <span className="dos-note" style={{ color: 'var(--rouge)' }}>{erreur}</span>}
    </>
  )
}

function EditeurModele({ modele, surChange }: { modele: ModeleContrat; surChange: () => void }) {
  const publie = modele.publieLe !== null
  const [titre, setTitre] = useState(modele.titre)
  const [corps, setCorps] = useState(modele.corps)
  const [mois, setMois] = useState(modele.exclusiviteMois === null ? '' : String(modele.exclusiviteMois))
  const [provisoire, setProvisoire] = useState(modele.provisoire)
  const [apercu, setApercu] = useState(publie)
  const [exemple, setExemple] = useState<keyof typeof IDENTITES_EXEMPLE | ''>('')
  const { occupe, erreur, info, lancer, signaler } = useAction()
  const controle = useMemo(() => controlerModele(corps), [corps])
  const rendu = useMemo(
    () => (exemple === '' ? null : apercuModele(corps, modele.kind, exemple)),
    [corps, exemple, modele.kind],
  )

  const champs = (): ChampsModele => {
    const n = mois.trim() === '' ? null : Number(mois)
    return {
      titre,
      corps,
      exclusiviteMois: n !== null && Number.isInteger(n) && n > 0 ? n : null,
      provisoire,
    }
  }

  function enregistrer() {
    if (!titre.trim()) return signaler('Le titre est obligatoire.')
    void lancer(async () => {
      await enregistrerBrouillonModele(modele.id, modele.kind, champs())
      surChange()
      return 'Brouillon enregistré.'
    })
  }

  function publier() {
    if (provisoire) {
      return signaler(
        'Ce contrat est marqué « texte provisoire » : remplacez le texte, puis décochez la case avant de publier.',
      )
    }
    if (!titre.trim()) return signaler('Le titre est obligatoire.')
    if (controle.inconnues.length > 0) {
      return signaler(
        `Marque(s) inconnue(s) : ${controle.inconnues.map((m) => `{{${m}}}`).join(', ')}. Le rendu échouerait pour tous les partenaires : corrigez-les avant de publier.`,
      )
    }
    if (controle.accolades.length > 0) {
      return signaler("Accolades « {{ » ou « }} » mal formées : une marque doit s'écrire exactement {{nom}}.")
    }
    if (modele.kind === 'commercant' && champs().exclusiviteMois === null) {
      return signaler("Un contrat commerçant exige une durée d'exclusivité (nombre entier de mois).")
    }
    const avertissements: string[] = []
    if (controle.crochets.length > 0) {
      avertissements.push(
        `Il reste ${controle.crochets.length} passage(s) entre crochets à compléter :\n  ${controle.crochets.slice(0, 8).join('\n  ')}${controle.crochets.length > 8 ? '\n  …' : ''}`,
      )
    }
    if (controle.sansDesignation) {
      avertissements.push("Le texte ne contient pas {{designation_partie}} : la partie ne sera pas désignée d'après son statut juridique.")
    }
    if (
      !window.confirm(
        `${avertissements.length ? `${avertissements.join('\n\n')}\n\n` : ''}Publier la version ${modele.version} ?\n\nElle deviendra le contrat à signer et ne pourra plus être modifiée.`,
      )
    ) {
      return
    }
    void lancer(async () => {
      await publierModele(modele.id, modele.kind, champs())
      surChange()
      return 'Version publiée : elle est désormais le contrat à signer.'
    })
  }

  return (
    <>
      <div className="sous-liens" style={{ marginBottom: 8 }}>
        <b>{LIBELLE_KIND[modele.kind]} — version {modele.version}</b>
        {publie ? (
          <span className="badge badge-green">Publiée le {dateCourte(modele.publieLe)}</span>
        ) : (
          <span className="badge badge-yellow">Brouillon</span>
        )}
      </div>
      {publie && (
        <p className="dos-note" style={{ marginBottom: 8 }}>
          Une version publiée n'est plus modifiable : créez un nouveau brouillon pour la faire évoluer.
        </p>
      )}

      <label>Titre</label>
      <input value={titre} disabled={publie} onChange={(e) => setTitre(e.target.value)} />

      {modele.kind === 'commercant' && (
        <>
          <label>Durée d'exclusivité (mois) — obligatoire pour publier</label>
          <input
            type="number"
            min={1}
            step={1}
            value={mois}
            disabled={publie}
            onChange={(e) => setMois(e.target.value)}
            style={{ maxWidth: 160 }}
          />
        </>
      )}

      <label className="sous-liens" style={{ margin: '10px 0 4px' }}>
        <input type="checkbox" checked={provisoire} disabled={publie} onChange={(e) => setProvisoire(e.target.checked)} />
        Texte provisoire (empêche la publication)
      </label>

      <div className="sous-liens" style={{ margin: '10px 0 4px' }}>
        <button className={`chip${!apercu ? ' active' : ''}`} disabled={publie} onClick={() => setApercu(false)}>
          Édition
        </button>
        <button className={`chip${apercu ? ' active' : ''}`} onClick={() => setApercu(true)}>
          Aperçu
        </button>
      </div>
      {apercu ? (
        <div className="dos-bloc" style={{ marginBottom: 0 }}>
          <div className="dos-champs" style={{ marginTop: 0, marginBottom: 8 }}>
            <label htmlFor="exemple-statut" style={{ margin: 0, fontSize: 12.5 }}>Rendu avec un exemple :</label>
            <select id="exemple-statut" value={exemple} onChange={(e) => setExemple(e.target.value as typeof exemple)}>
              <option value="">Texte brut (marques visibles)</option>
              {(Object.keys(IDENTITES_EXEMPLE) as Array<keyof typeof IDENTITES_EXEMPLE>)
                .filter((k) => modele.kind === 'commercant' || k !== 'societe')
                .map((k) => (
                  <option key={k} value={k}>{LIBELLE_STATUT[k]}</option>
                ))}
            </select>
          </div>
          {rendu?.erreur ? <div className="dos-erreur">{rendu.erreur}</div> : <MarkdownRendu source={rendu?.texte ?? corps} />}
        </div>
      ) : (
        <textarea
          rows={16}
          value={corps}
          disabled={publie}
          onChange={(e) => setCorps(e.target.value)}
          style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 12.5 }}
        />
      )}

      {!publie && <ControleModeleAffichage controle={controle} />}

      {!publie && (
        <div className="dos-champs">
          <button className="btn btn-outline btn-sm" disabled={occupe} onClick={enregistrer}>Enregistrer le brouillon</button>
          <button className="btn btn-accent btn-sm" disabled={occupe} onClick={publier}>Publier</button>
        </div>
      )}
      <Erreur texte={erreur} />
      <Info texte={info} />
    </>
  )
}

/** Avertissements affichés EN DIRECT sous l'éditeur : marques inconnues (bloquant), crochets restants. */
function ControleModeleAffichage({ controle }: { controle: ReturnType<typeof controlerModele> }) {
  return (
    <div>
      <p className="dos-note" style={{ marginTop: 6 }}>
        Marques disponibles :{' '}
        {MARQUES_PROPOSEES.map((m, i) => (
          <span key={m.marque}>
            {i > 0 && ' · '}
            <code title={m.sens}>{`{{${m.marque}}}`}</code>
          </span>
        ))}
        . Elles sont remplies automatiquement d'après le statut juridique du partenaire.
      </p>
      {controle.inconnues.length > 0 && (
        <div className="mod-avert bloque">
          <b>Marque(s) inconnue(s) :</b> {controle.inconnues.map((m) => <code key={m}>{`{{${m}}}`} </code>)}— le rendu échouerait
          pour tous les partenaires. Publication impossible tant qu'elles ne sont pas corrigées.
        </div>
      )}
      {controle.accolades.length > 0 && (
        <div className="mod-avert bloque">
          <b>Accolades mal formées</b> (« {'{{'} » ou « {'}}'} » isolés) : une marque s'écrit exactement <code>{'{{nom}}'}</code>.
        </div>
      )}
      {controle.crochets.length > 0 && (
        <div className="mod-avert info">
          <b>{controle.crochets.length} passage(s) entre crochets à compléter avant publication :</b>{' '}
          {controle.crochets.slice(0, 6).map((c, i) => <code key={i}>{c} </code>)}
          {controle.crochets.length > 6 && '…'}
        </div>
      )}
      {controle.sansDesignation && (
        <div className="mod-avert info">
          Ce texte ne contient pas <code>{'{{designation_partie}}'}</code> : la partie ne serait pas désignée d'après son statut juridique.
        </div>
      )}
    </div>
  )
}
